import { useState, useEffect, useCallback } from 'react';
import {
  ResponsiveContainer,
  LineChart, Line,
  BarChart, Bar,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import api from '../services/api';
import Layout from '../components/Layout';

// ── Brand palette for charts ─────────────────────────────────────────────────

const COLORS = ['#20C997', '#003479', '#8B5CF6', '#F59E0B', '#EF4444', '#06B6D4', '#EC4899', '#10B981'];
const PRIMARY = '#20C997';
const SECONDARY = '#003479';

// ── Date range options ───────────────────────────────────────────────────────

const RANGE_OPTIONS = [
  { label: 'Last 7 days',   value: '7d' },
  { label: 'Last 30 days',  value: '30d' },
  { label: 'Last 3 months', value: '3m' },
  { label: 'Last 6 months', value: '6m' },
  { label: 'All time',      value: 'all' },
];

// ── Shared components ────────────────────────────────────────────────────────

const ChartCard = ({ title, subtitle, children, className = '', loading }) => (
  <div className={`bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden ${className}`}>
    <div className="px-6 pt-5 pb-3 border-b border-gray-100">
      <h3 className="text-base font-bold text-gray-900">{title}</h3>
      {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
    </div>
    <div className="p-6">
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center gap-3">
            <svg className="animate-spin w-8 h-8 text-primary" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            <p className="text-sm text-gray-400">Loading data...</p>
          </div>
        </div>
      ) : children}
    </div>
  </div>
);

const StatCard = ({ label, value, icon, color, subtext }) => (
  <div className={`bg-gradient-to-br ${color} rounded-2xl p-5 text-white shadow-lg`}>
    <div className="flex items-center justify-between">
      <div>
        <p className="text-3xl font-bold">{value}</p>
        <p className="text-sm opacity-90 mt-0.5">{label}</p>
        {subtext && <p className="text-xs opacity-70 mt-1">{subtext}</p>}
      </div>
      <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center">
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} />
        </svg>
      </div>
    </div>
  </div>
);

const EmptyChart = ({ message }) => (
  <div className="flex flex-col items-center justify-center h-64 text-gray-400">
    <svg className="w-12 h-12 mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
    <p className="text-sm font-medium">{message || 'No data available'}</p>
    <p className="text-xs mt-1">Data will appear once activity is recorded</p>
  </div>
);

const CustomTooltip = ({ active, payload, label, valueLabel = 'Value' }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 px-4 py-3 text-sm">
      <p className="font-semibold text-gray-900 mb-1">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} className="text-gray-600" style={{ color: entry.color }}>
          {entry.name || valueLabel}: <span className="font-bold">{entry.value?.toLocaleString()}</span>
        </p>
      ))}
    </div>
  );
};

// ── Page skeleton ────────────────────────────────────────────────────────────

const PageSkeleton = () => (
  <Layout>
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="skeleton h-8 w-48" />
        <div className="flex gap-3">
          <div className="skeleton h-10 w-40 rounded-xl" />
          <div className="skeleton h-10 w-32 rounded-xl" />
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="rounded-2xl skeleton h-24" />
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-white rounded-2xl border border-gray-200 p-6">
            <div className="skeleton h-5 w-40 mb-4" />
            <div className="skeleton h-64 w-full rounded-xl" />
          </div>
        ))}
      </div>
    </div>
  </Layout>
);

// ── CSV export helper ────────────────────────────────────────────────────────

const downloadCsv = (data, filename) => {
  if (!data || !data.length) return;
  const keys = Object.keys(data[0]);
  const rows = [keys.join(',')];
  data.forEach((row) => {
    rows.push(keys.map((k) => {
      const v = row[k] ?? '';
      return typeof v === 'string' && v.includes(',') ? `"${v}"` : v;
    }).join(','));
  });
  const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

// ── Main Component ───────────────────────────────────────────────────────────

const AnalyticsPage = () => {
  const [range, setRange] = useState('6m');
  const [enrollmentData, setEnrollmentData] = useState(null);
  const [attendanceData, setAttendanceData] = useState(null);
  const [courseData, setCourseData] = useState(null);
  const [loadingEnrollment, setLoadingEnrollment] = useState(true);
  const [loadingAttendance, setLoadingAttendance] = useState(true);
  const [loadingCourses, setLoadingCourses] = useState(true);
  const [initialLoad, setInitialLoad] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);

  // Toast
  const [toast, setToast] = useState(null);

  // ── Fetch enrollment analytics ─────────────────────────────────────

  const fetchEnrollment = useCallback(async () => {
    setLoadingEnrollment(true);
    try {
      const res = await api.get('/api/analytics/enrollments', { params: { range } });
      setEnrollmentData(res.data);
    } catch {
      setEnrollmentData({
        monthly: [],
        by_course: [],
        by_batch: [],
        total_enrollments: 0,
        new_this_month: 0,
      });
    } finally {
      setLoadingEnrollment(false);
    }
  }, [range]);

  // ── Fetch attendance analytics ─────────────────────────────────────

  const fetchAttendance = useCallback(async () => {
    setLoadingAttendance(true);
    try {
      const res = await api.get('/api/analytics/attendance', { params: { range } });
      setAttendanceData(res.data);
    } catch {
      setAttendanceData({
        by_batch: [],
        daily_trend: [],
        overall_rate: 0,
        total_classes: 0,
      });
    } finally {
      setLoadingAttendance(false);
    }
  }, [range]);

  // ── Fetch course performance ───────────────────────────────────────

  const fetchCourses = useCallback(async () => {
    setLoadingCourses(true);
    try {
      const res = await api.get('/api/analytics/courses', { params: { range } });
      setCourseData(res.data);
    } catch {
      setCourseData({ courses: [], top_courses: [] });
    } finally {
      setLoadingCourses(false);
    }
  }, [range]);

  // ── Load all on mount / range change ───────────────────────────────

  useEffect(() => {
    const load = async () => {
      await Promise.all([fetchEnrollment(), fetchAttendance(), fetchCourses()]);
      setInitialLoad(false);
    };
    load();
  }, [fetchEnrollment, fetchAttendance, fetchCourses]);

  // ── Export handler ─────────────────────────────────────────────────

  const handleExport = async () => {
    setExportLoading(true);
    try {
      // Try server-side export first
      const res = await api.get('/api/exports/analytics', {
        params: { range },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `analytics-${range}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // Fallback: client-side CSV from loaded data
      const rows = [];
      if (enrollmentData?.monthly?.length) {
        rows.push({ section: 'Enrollment Trends', period: '', value: '' });
        enrollmentData.monthly.forEach((r) =>
          rows.push({ section: '', period: r.month || r.label, value: r.count || r.enrollments })
        );
      }
      if (enrollmentData?.by_course?.length) {
        rows.push({ section: 'Students by Course', period: '', value: '' });
        enrollmentData.by_course.forEach((r) =>
          rows.push({ section: '', period: r.name || r.course, value: r.value || r.count })
        );
      }
      if (attendanceData?.by_batch?.length) {
        rows.push({ section: 'Attendance by Batch', period: '', value: '' });
        attendanceData.by_batch.forEach((r) =>
          rows.push({ section: '', period: r.name || r.batch, value: r.rate || r.attendance_rate })
        );
      }
      if (courseData?.courses?.length) {
        rows.push({ section: 'Course Performance', period: '', value: '' });
        courseData.courses.forEach((r) =>
          rows.push({ section: '', period: r.title || r.name, value: `${r.enrollment_count || 0} enrolled` })
        );
      }
      if (rows.length) {
        downloadCsv(rows, `analytics-${range}.csv`);
      }
    } finally {
      setExportLoading(false);
    }
  };

  // ── Derived data ───────────────────────────────────────────────────

  const enrollMonthly = enrollmentData?.monthly || [];
  const enrollByCourse = enrollmentData?.by_course || [];
  const enrollByBatch = enrollmentData?.by_batch || [];
  const totalEnrollments = enrollmentData?.total_enrollments ?? 0;
  const newThisMonth = enrollmentData?.new_this_month ?? 0;

  const attendByBatch = attendanceData?.by_batch || [];
  const attendDaily = attendanceData?.daily_trend || [];
  const overallRate = attendanceData?.overall_rate ?? 0;
  const totalClasses = attendanceData?.total_classes ?? 0;

  const courseList = courseData?.courses || [];
  const topCourses = courseData?.top_courses || courseList.slice(0, 3);

  // ── Loading ────────────────────────────────────────────────────────

  if (initialLoad) return <PageSkeleton />;

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <Layout>
      {toast && (
        <div className={`fixed top-20 right-4 z-50 flex items-center gap-3 px-5 py-3 rounded-xl border shadow-lg animate-slide-down ${toast.type === 'error' ? 'bg-red-50 border-red-200 text-red-800' : 'bg-emerald-50 border-emerald-200 text-emerald-800'}`}>
          <span className="text-sm font-medium">{toast.message}</span>
          <button onClick={() => setToast(null)} className="ml-2 opacity-60 hover:opacity-100">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      <div className="space-y-6 animate-fade-in">
        {/* ── Header ──────────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
            <p className="text-sm text-gray-500 mt-1">Insights across enrollments, attendance, and courses</p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={range}
              onChange={(e) => setRange(e.target.value)}
              className="px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all text-sm bg-white"
            >
              {RANGE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            <button
              onClick={handleExport}
              disabled={exportLoading}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-secondary text-white text-sm font-semibold rounded-xl hover:bg-secondary/90 transition-colors shadow-sm disabled:opacity-50"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {exportLoading ? 'Exporting...' : 'Export CSV'}
            </button>
          </div>
        </div>

        {/* ── Top Stats ───────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatCard
            label="Total Enrollments"
            value={totalEnrollments.toLocaleString()}
            icon="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
            color="from-[#003479] to-[#0052b4]"
            subtext={newThisMonth ? `+${newThisMonth} this month` : undefined}
          />
          <StatCard
            label="Attendance Rate"
            value={`${Math.round(overallRate)}%`}
            icon="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            color="from-[#20C997] to-emerald-600"
            subtext={`across ${totalClasses} classes`}
          />
          <StatCard
            label="Active Courses"
            value={(courseList.length || 0).toLocaleString()}
            icon="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
            color="from-violet-500 to-purple-600"
          />
          <StatCard
            label="Total Classes"
            value={totalClasses.toLocaleString()}
            icon="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
            color="from-blue-500 to-cyan-600"
          />
        </div>

        {/* ── Top Courses (quick cards) ───────────────────────────────── */}
        {topCourses.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {topCourses.slice(0, 3).map((c, i) => {
              const medals = ['from-yellow-400 to-amber-500', 'from-gray-300 to-gray-400', 'from-amber-600 to-orange-700'];
              const labels = ['Most Popular', '2nd Popular', '3rd Popular'];
              return (
                <div key={c.id || i} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${medals[i] || medals[2]} flex items-center justify-center text-white font-bold text-sm shadow-md`}>
                    #{i + 1}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-gray-900 truncate">{c.title || c.name}</p>
                    <p className="text-xs text-gray-500">{labels[i]} &middot; {c.enrollment_count ?? c.students ?? 0} students</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════
            ENROLLMENT ANALYTICS
           ══════════════════════════════════════════════════════════════ */}

        <div>
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
            Enrollment Analytics
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Enrollment trend line */}
            <ChartCard title="Enrollment Trends" subtitle="Monthly enrollments over time" loading={loadingEnrollment}>
              {enrollMonthly.length === 0 ? (
                <EmptyChart message="No enrollment data yet" />
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={enrollMonthly} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip content={<CustomTooltip valueLabel="Enrollments" />} />
                    <Line
                      type="monotone"
                      dataKey={enrollMonthly[0]?.enrollments !== undefined ? 'enrollments' : 'count'}
                      name="Enrollments"
                      stroke={PRIMARY}
                      strokeWidth={3}
                      dot={{ r: 5, fill: PRIMARY, strokeWidth: 2, stroke: '#fff' }}
                      activeDot={{ r: 7, stroke: PRIMARY, strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            {/* Students by course pie */}
            <ChartCard title="Students by Course" subtitle="Distribution across courses" loading={loadingEnrollment}>
              {enrollByCourse.length === 0 ? (
                <EmptyChart message="No course enrollment data" />
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={enrollByCourse}
                      dataKey={enrollByCourse[0]?.value !== undefined ? 'value' : 'count'}
                      nameKey={enrollByCourse[0]?.name !== undefined ? 'name' : 'course'}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      innerRadius={50}
                      paddingAngle={3}
                      label={({ name, course, percent }) => `${name || course} ${(percent * 100).toFixed(0)}%`}
                      labelLine={{ strokeWidth: 1 }}
                    >
                      {enrollByCourse.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip valueLabel="Students" />} />
                    <Legend
                      verticalAlign="bottom"
                      iconType="circle"
                      iconSize={8}
                      formatter={(value) => <span className="text-xs text-gray-600">{value}</span>}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            {/* Enrollments by batch bar */}
            <ChartCard title="Enrollments by Batch" subtitle="Student count per batch" loading={loadingEnrollment} className="lg:col-span-2">
              {enrollByBatch.length === 0 ? (
                <EmptyChart message="No batch enrollment data" />
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={enrollByBatch} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey={enrollByBatch[0]?.name !== undefined ? 'name' : 'batch'}
                      tick={{ fontSize: 11, fill: '#6b7280' }}
                      axisLine={false}
                      tickLine={false}
                      interval={0}
                      angle={-20}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip content={<CustomTooltip valueLabel="Students" />} />
                    <Bar
                      dataKey={enrollByBatch[0]?.count !== undefined ? 'count' : 'enrollments'}
                      name="Students"
                      radius={[8, 8, 0, 0]}
                    >
                      {enrollByBatch.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════
            ATTENDANCE ANALYTICS
           ══════════════════════════════════════════════════════════════ */}

        <div>
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Attendance Analytics
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Overall attendance donut */}
            <ChartCard title="Overall Attendance" subtitle="Average across all batches" loading={loadingAttendance}>
              <div className="flex flex-col items-center justify-center h-64">
                <div className="relative w-44 h-44">
                  <svg className="w-full h-full -rotate-90" viewBox="0 0 160 160">
                    <circle cx="80" cy="80" r="70" fill="none" stroke="#f3f4f6" strokeWidth="14" />
                    <circle
                      cx="80" cy="80" r="70" fill="none"
                      stroke={overallRate >= 75 ? PRIMARY : overallRate >= 50 ? '#F59E0B' : '#EF4444'}
                      strokeWidth="14"
                      strokeLinecap="round"
                      strokeDasharray={`${(overallRate / 100) * 440} 440`}
                      className="transition-all duration-1000 ease-out"
                    />
                  </svg>
                  <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-4xl font-bold text-gray-900">{Math.round(overallRate)}%</span>
                    <span className="text-xs text-gray-500 mt-0.5">attendance</span>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-4">{totalClasses} total classes tracked</p>
              </div>
            </ChartCard>

            {/* Attendance by batch bar */}
            <ChartCard title="Attendance by Batch" subtitle="Attendance rate per batch" loading={loadingAttendance}>
              {attendByBatch.length === 0 ? (
                <EmptyChart message="No attendance data yet" />
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={attendByBatch} layout="vertical" margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                    <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} unit="%" />
                    <YAxis
                      dataKey={attendByBatch[0]?.name !== undefined ? 'name' : 'batch'}
                      type="category"
                      tick={{ fontSize: 11, fill: '#6b7280' }}
                      axisLine={false}
                      tickLine={false}
                      width={100}
                    />
                    <Tooltip content={<CustomTooltip valueLabel="Rate" />} formatter={(v) => `${v}%`} />
                    <Bar
                      dataKey={attendByBatch[0]?.rate !== undefined ? 'rate' : 'attendance_rate'}
                      name="Attendance %"
                      radius={[0, 8, 8, 0]}
                      barSize={20}
                    >
                      {attendByBatch.map((entry, i) => {
                        const rate = entry.rate ?? entry.attendance_rate ?? 0;
                        return <Cell key={i} fill={rate >= 75 ? PRIMARY : rate >= 50 ? '#F59E0B' : '#EF4444'} />;
                      })}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </ChartCard>

            {/* Attendance trend line */}
            <ChartCard title="Attendance Trends" subtitle="Daily attendance over recent period" loading={loadingAttendance} className="lg:col-span-2">
              {attendDaily.length === 0 ? (
                <EmptyChart message="No daily attendance data" />
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={attendDaily} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey={attendDaily[0]?.date !== undefined ? 'date' : 'day'} tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 12, fill: '#6b7280' }} axisLine={false} tickLine={false} unit="%" />
                    <Tooltip content={<CustomTooltip valueLabel="Attendance" />} formatter={(v) => `${v}%`} />
                    <Line
                      type="monotone"
                      dataKey={attendDaily[0]?.rate !== undefined ? 'rate' : 'attendance_rate'}
                      name="Attendance %"
                      stroke={SECONDARY}
                      strokeWidth={2.5}
                      dot={{ r: 3, fill: SECONDARY, strokeWidth: 2, stroke: '#fff' }}
                      activeDot={{ r: 6, stroke: SECONDARY, strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </ChartCard>
          </div>
        </div>

        {/* ══════════════════════════════════════════════════════════════
            COURSE PERFORMANCE TABLE
           ══════════════════════════════════════════════════════════════ */}

        <div>
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
            </svg>
            Course Performance
          </h2>
          <ChartCard title="All Courses" subtitle="Performance metrics for each course" loading={loadingCourses}>
            {courseList.length === 0 ? (
              <EmptyChart message="No course data available" />
            ) : (
              <div className="overflow-x-auto -mx-6">
                <table className="w-full min-w-[600px]">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Course</th>
                      <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Batches</th>
                      <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Enrolled</th>
                      <th className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Completion Rate</th>
                      <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-6 py-3">Performance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {courseList.map((c, i) => {
                      const completionRate = c.completion_rate ?? 0;
                      return (
                        <tr key={c.id || i} className="hover:bg-gray-50/60 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${COLORS[i % COLORS.length]}20` }}>
                                <span className="text-sm font-bold" style={{ color: COLORS[i % COLORS.length] }}>
                                  {(c.title || c.name || '?')[0].toUpperCase()}
                                </span>
                              </div>
                              <span className="text-sm font-semibold text-gray-900 truncate max-w-[200px]">
                                {c.title || c.name}
                              </span>
                            </div>
                          </td>
                          <td className="text-center px-4 py-4">
                            <span className="inline-flex items-center justify-center text-sm font-semibold text-blue-700 bg-blue-50 px-3 py-1 rounded-full min-w-[36px]">
                              {c.batch_count ?? 0}
                            </span>
                          </td>
                          <td className="text-center px-4 py-4">
                            <span className="inline-flex items-center justify-center text-sm font-semibold text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full min-w-[36px]">
                              {c.enrollment_count ?? c.students ?? 0}
                            </span>
                          </td>
                          <td className="text-center px-4 py-4">
                            <span className="text-sm font-semibold text-gray-900">{Math.round(completionRate)}%</span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2 justify-end">
                              <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all duration-700 ease-out"
                                  style={{
                                    width: `${Math.min(completionRate, 100)}%`,
                                    backgroundColor: completionRate >= 75 ? PRIMARY : completionRate >= 50 ? '#F59E0B' : '#EF4444',
                                  }}
                                />
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </ChartCard>
        </div>
      </div>
    </Layout>
  );
};

export default AnalyticsPage;
